/**
 * Export iCalendar (RFC 5545).
 *
 * C'est le seul endroit du projet qui produit un format destiné à d'autres
 * logiciels, et ces logiciels sont intransigeants : Google Agenda refuse en
 * silence un flux mal plié, et affiche « aucun événement » sans dire pourquoi.
 * D'où le soin apporté ici à quatre détails qui n'en sont pas :
 *
 *  1. **Fins de ligne CRLF.** La RFC les impose. Un flux en `\n` seul est
 *     accepté par certains clients et rejeté par d'autres — donc à éviter.
 *  2. **Pliage à 75 *octets*, pas 75 caractères.** « é » compte pour deux. Une
 *     ligne pliée au caractère 75 dépasse la limite dès qu'il y a des accents,
 *     et couper au milieu d'une séquence UTF-8 produit un octet invalide. Le
 *     pliage ci-dessous mesure en octets et ne coupe jamais un caractère.
 *  3. **`DTEND` est exclusif** pour un événement en journée entière : une tâche
 *     du 20 août se déclare du 20 au 21, sinon elle n'apparaît pas.
 *  4. **`UID` stable.** S'il change d'un export à l'autre, le client ne met pas
 *     l'événement à jour : il en crée un second et l'agenda se remplit de
 *     doublons.
 *
 * Choix assumé : **aucun `VALARM`**. Foyer envoie déjà ses rappels par trois
 * canaux ; ajouter des alarmes ici doublerait chaque notification, une fois par
 * l'application et une fois par l'agenda.
 */
import { parties } from './dates.js';
const PLIAGE_PREMIERE = 75;
// Les lignes de continuation commencent par une espace, qui consomme un octet.
const PLIAGE_SUIVANTES = 74;
const encodeur = new TextEncoder();
/**
 * Plie une ligne selon la RFC 5545, en comptant les octets et en itérant par
 * point de code — jamais par unité UTF-16 — pour ne casser ni un accent ni un
 * emoji.
 */
export function plieLigne(ligne) {
    const morceaux = [];
    let courant = '';
    let octets = 0;
    let budget = PLIAGE_PREMIERE;
    for (const car of ligne) {
        const taille = encodeur.encode(car).length;
        if (octets + taille > budget) {
            morceaux.push(courant);
            courant = '';
            octets = 0;
            budget = PLIAGE_SUIVANTES;
        }
        courant += car;
        octets += taille;
    }
    morceaux.push(courant);
    return morceaux.join('\r\n ');
}
/**
 * Échappe une valeur TEXT. L'ordre compte : la barre oblique inverse d'abord,
 * sinon on échapperait les échappements qu'on vient de poser. Le deux-points,
 * lui, ne s'échappe pas dans une valeur TEXT — l'échapper ferait apparaître un
 * « \: » littéral chez le lecteur.
 */
export function echappeTexte(v) {
    return v
        .replace(/\\/g, '\\\\')
        .replace(/;/g, '\\;')
        .replace(/,/g, '\\,')
        .replace(/\r\n|\n|\r/g, '\\n');
}
/** `2026-08-20` → `20260820`, la forme DATE de la RFC. */
function dateCompacte(d) {
    const p = parties(d);
    return `${String(p.annee).padStart(4, '0')}${String(p.mois).padStart(2, '0')}${String(p.jour).padStart(2, '0')}`;
}
/** Lendemain en forme compacte, pour un `DTEND` exclusif. */
function lendemainCompact(d) {
    const p = parties(d);
    const t = Date.UTC(p.annee, p.mois - 1, p.jour + 1);
    const j = new Date(t);
    return `${j.getUTCFullYear()}${String(j.getUTCMonth() + 1).padStart(2, '0')}${String(j.getUTCDate()).padStart(2, '0')}`;
}
/** Horodatage UTC `YYYYMMDDTHHMMSSZ`. */
export function horodatageUtc(instant) {
    const p = (n) => String(n).padStart(2, '0');
    return (`${instant.getUTCFullYear()}${p(instant.getUTCMonth() + 1)}${p(instant.getUTCDate())}` +
        `T${p(instant.getUTCHours())}${p(instant.getUTCMinutes())}${p(instant.getUTCSeconds())}Z`);
}
/**
 * Une tâche calée sur les vacances scolaires ne peut pas être décrite par une
 * RRULE : ses dates viennent du calendrier du ministère, qui ne suit aucune
 * règle calculable — le milieu des vacances de février ne tombe pas le même
 * jour d'une année sur l'autre. Exporter `FREQ=YEARLY` reviendrait à affirmer
 * une date fausse pour toutes les occurrences suivantes. On n'exporte donc que
 * l'occurrence connue, et l'agenda se remettra à jour au prochain rafraîchissement.
 */
function rruleExportable(e) {
    if (e.ancrageVacances)
        return null;
    return e.rrule;
}
function evenement(e, o, dtstamp) {
    const lignes = ['BEGIN:VEVENT'];
    lignes.push(`UID:${e.id}@${o.domaine}`);
    lignes.push(`DTSTAMP:${dtstamp}`);
    lignes.push(`DTSTART;VALUE=DATE:${dateCompacte(e.echeance)}`);
    lignes.push(`DTEND;VALUE=DATE:${lendemainCompact(e.echeance)}`);
    // Une date incertaine doit se voir dans l'agenda, pas seulement dans Foyer :
    // sinon l'utilisateur cale un rendez-vous dessus.
    const titre = e.echeanceAConfirmer ? `${e.titre} (date à confirmer)` : e.titre;
    lignes.push(`SUMMARY:${echappeTexte(titre)}`);
    const description = [
        e.notes,
        e.reference ? `Référence : ${e.reference}` : null,
        e.ancrageVacances ? 'Date calée sur le calendrier scolaire.' : null,
        e.echeanceAConfirmer ? 'Date prévisionnelle, à confirmer auprès de la source officielle.' : null,
    ]
        .filter((x) => Boolean(x))
        .join('\n');
    if (description)
        lignes.push(`DESCRIPTION:${echappeTexte(description)}`);
    lignes.push(`CATEGORIES:${echappeTexte(e.categorie.toUpperCase())}`);
    const rrule = rruleExportable(e);
    if (rrule)
        lignes.push(`RRULE:${rrule}`);
    // Une échéance n'occupe pas la journée : elle ne doit pas faire apparaître
    // l'utilisateur comme indisponible dans un agenda partagé.
    lignes.push('TRANSP:TRANSPARENT');
    lignes.push('END:VEVENT');
    return lignes;
}
/**
 * Construit le flux complet. Les tâches sans échéance sont écartées : une liste
 * de travaux sans date n'a rien à faire dans un agenda.
 */
export function construisIcal(evenements, o) {
    const dtstamp = horodatageUtc(o.horodatage);
    const lignes = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//Foyer//Calendrier du foyer//FR',
        'CALSCALE:GREGORIAN',
        'METHOD:PUBLISH',
        `X-WR-CALNAME:${echappeTexte(o.nomCalendrier)}`,
        'X-WR-TIMEZONE:Europe/Paris',
        // Les deux formes coexistent : la normalisée et celle qu'Outlook comprend.
        'REFRESH-INTERVAL;VALUE=DURATION:PT12H',
        'X-PUBLISHED-TTL:PT12H',
    ];
    for (const e of evenements) {
        if (!e.echeance)
            continue;
        lignes.push(...evenement(e, o, dtstamp));
    }
    lignes.push('END:VCALENDAR');
    return `${lignes.map(plieLigne).join('\r\n')}\r\n`;
}
//# sourceMappingURL=ical.js.map